/**
 * ListingsContent - Enhanced Properties/Listings Page Content
 * 
 * Uses the full Listings API data to display:
 * - Listing type badges (Sale/Rent/Auction)
 * - Proper pricing based on listing type
 * - Rental terms and auction details
 * - Advanced filtering and sorting
 */

'use client';

import { useState, useMemo, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import type { ListingOnChannel } from '@/lib/api/clients/listings';
import type { PropertyDevelopmentProject } from '@/lib/api/clients/projects';
import {
  ListingGrid,
  ListingFilters,
  defaultFilters,
  transformListingToCardData,
} from '@/components/listings';
import type { ListingFiltersState, ListingCardData } from '@/components/listings';

const SLUG_TO_CODE: Record<string, string> = {
  villa: 'VILLA',
  residential: 'RESIDENTIAL',
  apartment: 'APARTMENT',
  commercial: 'COMMERCIAL',
  land: 'LAND',
  house: 'HOUSE',
  penthouse: 'PENTHOUSE',
};

interface Props {
  listings: ListingOnChannel[];
  locale: string;
  messages: Record<string, unknown>;
  basePath: string;
  propertyTypes?: Array<{ id: string; name: string; code?: string }>;
  initialPropertyTypeSlug?: string | null;
  initialMinBedrooms?: number | null;
  initialMinBathrooms?: number | null;
  developmentProjects?: PropertyDevelopmentProject[];
}

export function ListingsContent({
  listings,
  locale,
  messages,
  basePath,
  propertyTypes = [],
  initialPropertyTypeSlug = null,
  initialMinBedrooms = null,
  initialMinBathrooms = null,
  developmentProjects = [],
}: Props) {
  // Transform API listings to card data
  const listingCardData = useMemo(() => {
    return listings
      .map(transformListingToCardData)
      .filter((l): l is ListingCardData => l !== null);
  }, [listings]);

  // Build a set of development IDs that have project cards
  const developmentIdSet = useMemo(() => {
    return new Set(developmentProjects.map((p) => p.id));
  }, [developmentProjects]);

  // Separate standalone listings from development-grouped listings
  const standaloneListings = useMemo(() => {
    if (developmentIdSet.size === 0) return listingCardData;
    return listingCardData.filter((l) => !l.development || !developmentIdSet.has(l.development.id));
  }, [listingCardData, developmentIdSet]);

  // Resolve the URL slug to a property type ID using multiple strategies:
  // 1. Match against propertyTypes from the getPropertyTypes() API (code-based)
  // 2. Fallback: match slug against propertyType.name in the actual listing data
  const resolvedTypeId = useMemo(() => {
    if (!initialPropertyTypeSlug) return null;
    const slugLower = initialPropertyTypeSlug.toLowerCase();

    const code = SLUG_TO_CODE[slugLower];
    if (code) {
      const apiMatch = propertyTypes.find(pt => pt.code?.toUpperCase() === code);
      if (apiMatch) return apiMatch.id;
    }

    const nameMatch = listingCardData.find(
      l => l.propertyType?.name?.toLowerCase() === slugLower
    );
    return nameMatch?.propertyType?.id ?? null;
  }, [initialPropertyTypeSlug, propertyTypes, listingCardData]);

  const [filters, setFilters] = useState<ListingFiltersState>(() => ({
    ...defaultFilters,
    propertyTypeId: null,
    minBedrooms: initialMinBedrooms ?? null,
    minBathrooms: initialMinBathrooms ?? null,
  }));

  useEffect(() => {
    if (resolvedTypeId && filters.propertyTypeId !== resolvedTypeId) {
      setFilters(prev => ({ ...prev, propertyTypeId: resolvedTypeId }));
    }
  }, [resolvedTypeId]);
  
  // Apply filters to standalone listings only (development units are hidden)
  const filteredListings = useMemo(() => {
    return standaloneListings
      .filter((listing) => {
        // Search filter
        if (filters.search) {
          const search = filters.search.toLowerCase();
          if (!listing.property.name.toLowerCase().includes(search)) {
            return false;
          }
        }
        
        // Listing type filter
        if (filters.listingType !== 'ALL' && listing.listingType !== filters.listingType) {
          return false;
        }
        
        // Status filter
        if (filters.status !== 'ALL' && listing.status !== filters.status) {
          return false;
        }
        
        // Price filter
        if (filters.minPrice !== null && (!listing.price || listing.price.amount < filters.minPrice)) {
          return false;
        }
        if (filters.maxPrice !== null && (!listing.price || listing.price.amount > filters.maxPrice)) {
          return false;
        }
        
        // Bedrooms filter
        if (filters.minBedrooms !== null && (listing.property.bedrooms === null || listing.property.bedrooms < filters.minBedrooms)) {
          return false;
        }
        
        // Bathrooms filter
        if (filters.minBathrooms !== null && (listing.property.bathrooms === null || listing.property.bathrooms < filters.minBathrooms)) {
          return false;
        }
        
        // Property type filter
        if (filters.propertyTypeId !== null && listing.propertyType?.id !== filters.propertyTypeId) {
          return false;
        }
        
        return true;
      })
      .sort((a, b) => {
        switch (filters.sortBy) {
          case 'newest':
            return (b.listedDate || '').localeCompare(a.listedDate || '');
          case 'price_asc':
            return (a.price?.amount || 0) - (b.price?.amount || 0);
          case 'price_desc':
            return (b.price?.amount || 0) - (a.price?.amount || 0);
          case 'bedrooms':
            return (b.property.bedrooms || 0) - (a.property.bedrooms || 0);
          default:
            return 0;
        }
      });
  }, [standaloneListings, filters]);

  // Filter development projects by search term if applicable
  const filteredProjects = useMemo(() => {
    if (developmentProjects.length === 0) return [];
    if (!filters.search) return developmentProjects;
    const search = filters.search.toLowerCase();
    return developmentProjects.filter((p) =>
      p.name.toLowerCase().includes(search) ||
      p.description?.toLowerCase().includes(search)
    );
  }, [developmentProjects, filters.search]);

  // Get translated messages
  const propertiesMessages = (messages.properties || {}) as Record<string, string>;
  const emptyMessage = propertiesMessages.noListings || 'No listings available';
  const emptyDescription = propertiesMessages.noListingsDescription || 
    'Check back later for new property listings.';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-primary/5 to-gray-50 pt-24 pb-12 px-4 md:px-8">
        <div className="container mx-auto max-w-7xl">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {propertiesMessages.title || 'Property Listings'}
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl">
            {propertiesMessages.subtitle || 
              'Discover your perfect property from our curated collection of homes, apartments, and investment opportunities.'}
          </p>
          
          {/* Stats */}
          <div className="flex flex-wrap gap-6 mt-8">
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-primary">{listings.length}</span>
              <span className="text-gray-600">Total Listings</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-emerald-600">
                {listings.filter(l => l.listing?.listingType === 'SALE').length}
              </span>
              <span className="text-gray-600">For Sale</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-blue-600">
                {listings.filter(l => 
                  l.listing?.listingType === 'RENTAL' || 
                  l.listing?.listingType === 'HOLIDAY_RENTAL'
                ).length}
              </span>
              <span className="text-gray-600">For Rent</span>
            </div>
          </div>
        </div>
      </section>
      
      {/* Filters */}
      <section className="sticky top-16 z-30 bg-white border-b border-gray-200 shadow-sm">
        <div className="container mx-auto max-w-7xl px-4 md:px-8 py-4">
          <ListingFilters
            filters={filters}
            onChange={setFilters}
            propertyTypes={propertyTypes}
            showAdvanced={true}
          />
        </div>
      </section>
      
      {/* Results */}
      <section className="py-8 px-4 md:px-8">
        <div className="container mx-auto max-w-7xl">
          {/* Results count */}
          <div className="flex items-center justify-between mb-6">
            <p className="text-gray-600">
              Showing <span className="font-semibold text-gray-900">{filteredListings.length + filteredProjects.length}</span> 
              {(filteredListings.length + filteredProjects.length) === 1 ? ' listing' : ' listings'}
              {filters.listingType !== 'ALL' && (
                <span className="text-primary"> • {filters.listingType}</span>
              )}
            </p>
          </div>

          {/* Development Project Cards */}
          {filteredProjects.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {filteredProjects.map((project) => (
                <Link
                  key={project.id}
                  href={`${basePath}/projects/${project.slug || project.id}`}
                  className="group block rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 bg-white border border-gray-100 hover:-translate-y-1"
                >
                  <div className="relative aspect-[16/9] bg-gray-100">
                    {project.heroImageUrl ? (
                      <Image
                        src={project.heroImageUrl}
                        alt={project.name}
                        fill
                        className="object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400 bg-gray-200">
                        <span>No Image</span>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                    <div className="absolute top-4 left-4 px-3 py-1.5 text-xs font-bold rounded-full bg-emerald-600 text-white shadow-lg">
                      Development
                    </div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="text-xl font-bold text-white mb-1">{project.name}</h3>
                      {project.displayPriceText && (
                        <p className="text-white/80 text-sm">{project.displayPriceText}</p>
                      )}
                      {project.availableUnits != null && project.totalUnits != null && (
                        <p className="text-white/70 text-sm mt-1">
                          {project.availableUnits} of {project.totalUnits} units available
                        </p>
                      )}
                    </div>
                  </div>
                  {project.description && (
                    <div className="p-4">
                      <p className="text-sm text-gray-600 line-clamp-2">{project.description}</p>
                    </div>
                  )}
                </Link>
              ))}
            </div>
          )}
          
          {/* Listings Grid (standalone listings only) */}
          <ListingGrid
            listings={filteredListings}
            basePath={basePath}
            variant="grid"
            columns={3}
            showAgent={false}
            showStatus={true}
            locale={locale}
            emptyMessage={filteredProjects.length > 0 ? '' : emptyMessage}
            emptyDescription={filteredProjects.length > 0 ? '' : emptyDescription}
          />
        </div>
      </section>
    </div>
  );
}

export default ListingsContent;
